/**
 * Global 404 page.
 *
 * Wired at `router.tsx` (`defaultNotFoundComponent`) and `__root.tsx`
 * (`notFoundComponent`), both inside the root `RegistryProvider`, so atoms are
 * readable. On the ClawInstall property (identity resolved) it renders the
 * design-board customer theme with identity-driven branding; on every other host
 * (`identity === null`, FlirtMachine / Orca) it keeps today's neutral markup.
 *
 * It renders its own `.theme-clawinstall` ancestor carrying `data-mode` (the
 * customer palette's dark selectors are descendant-based) and uses a plain
 * `<a href="/">` — the public root on every ClawInstall host — rather than a
 * router `Link`, so it stays usable without route context.
 *
 * effect-atom only — no useState/useEffect.
 *
 * @since 1.0.0
 */
import { useAtomValue } from "@effect-atom/atom-react";
import { brandIdentityAtom } from "~/lib/atoms/brand-identity.ts";
import { clawInstallModeAtom } from "~/lib/atoms/clawinstall-theme.ts";

export function NotFound() {
  const identity = useAtomValue(brandIdentityAtom);
  const mode = useAtomValue(clawInstallModeAtom);

  if (identity === null) {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-bold">404 - Page Not Found</h1>
        <p className="mt-2">The page you're looking for doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="theme-clawinstall" data-mode={mode} suppressHydrationWarning>
      <div className="theme-clawinstall-customer flex min-h-screen flex-col items-center justify-center gap-4 bg-surface px-6 text-center font-sans text-on-surface">
        <span
          aria-hidden="true"
          className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#09090b] text-xl font-bold leading-none text-white"
        >
          {identity.monogram}
        </span>
        <p className="font-sans text-[10px] font-bold uppercase tracking-[0.16em] text-secondary">404</p>
        <h1 className="font-headline-lg text-headline-lg tracking-tighter text-on-surface">Page not found</h1>
        <p className="max-w-md font-sans text-body-md text-on-surface-variant">
          The page you're looking for doesn't exist or has moved.
        </p>
        <a
          href="/"
          className="rounded-lg border border-outline-variant bg-surface-container-lowest px-4 py-2.5 font-sans text-sm font-bold text-on-surface transition-colors hover:bg-surface-container"
        >
          Back to {identity.displayName}
        </a>
      </div>
    </div>
  );
}
